import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useCreateTelegramStarsInvoiceLink } from '@Global/apollo/mutation/createTelegramStarsInvoiceLink/useCreateTelegramStarsInvoiceLink';
import { useSubmitBet } from '@Global/apollo/mutation/submitBet/useSubmitBet';
import { useGetBettingGame } from '@Global/apollo/query/getBettingGame/useGetBettingGame';
import { BattleBetOutcome, BattleData } from '@Global/apollo/query/getBettingGame/useGetBettingGame';
import { useGetBettingGames } from '@Global/apollo/query/getBettingGames/useGetBettingGames';
import { BettingGame, Currency, GameLifecycleStage } from '@Global/apollo/types';
import { Match } from '@Global/apollo/types';
import { DEFAULT_RAKE } from '@Global/consts';
import { useConvertCurrencies } from '@Global/hooks/useConvertCurrencies';
import { Pairs } from '@Global/store/appContext';
import { BetFormState } from '@Content/pages/Battles/BattleDetailsPage/types';

// ==================== BATTLES CONTEXT ====================

// Тип для данных контекста баттлов
export interface BattlesContextData {
  // Данные для детальной страницы баттла
  currentBattle: BattleData | null;
  battleLoading: boolean;
  battleError: ApolloError | undefined | null;
  loadBattle: (battleId: string) => void;
  refetchBattle: () => Promise<void>;
  loadPlayerStats: (playerId: string) => void;
  isLoadingMoreStats: boolean;
  currentBattleId: string | null;
  loadingMoreOrders: boolean;

  // Данные для списка баттлов
  battlesList: BettingGame[];
  battlesListLoading: boolean;
  battlesListError: ApolloError | undefined | null;
  loadBattlesList: (params: { lifecycle?: GameLifecycleStage[]; myBattles?: boolean }) => void;
  refreshBattlesList: () => Promise<void>;
  hasMoreBattles: boolean;
  loadMoreBattles: () => Promise<void>;
  loadingMoreBattles: boolean;

  // Функции для работы с призами
  getBattleOutcomeState: (battle: BattleData | null) => {
    hasWinningBet: boolean;
    hasUnclaimedGifts: boolean;
    winningOutcome: BattleBetOutcome | null;
  };

  // Функция для конвертации суммы в другие валюты
  convertAmount: (amount: string, from: Currency, to: Currency) => number;
  // Функция для получения баттла с обновлённой статистикой
  fetchBettingGameWithStats: (battleId: string | null) => Promise<void>;
}

const defaultLifecycleFilter = [
  GameLifecycleStage.Upcoming,
  GameLifecycleStage.Ongoing,
  GameLifecycleStage.Finished,
  GameLifecycleStage.Canceled,
];

// Создаем контекст баттлов
export const BattlesContext = createContext<BattlesContextData | null>(null);

// Хук для использования контекста баттлов
export const useBattles = () => {
  const context = useContext(BattlesContext);
  if (!context) {
    throw new Error('useBattles must be used within a BattlesProvider');
  }
  return context;
};

// ==================== BETS CONTEXT ====================

// Тип для результата размещения ставки
export interface BetPlacementResult {
  success: boolean;
  isEnoughBalance: boolean;
  error?: Error;
  isStarsPayment?: boolean; // Флаг для оплаты через Stars
  invoiceLink?: string; // Ссылка на invoice для Stars
}

// Тип для данных о размещенной ставке
export interface PlacedBetData {
  amount: string;
  currency: Currency;
  player: {
    id: string;
    name: string;
    image: string;
    position: string;
    cardId: string;
  };
  totalBank: string;
}

// Тип для данных контекста ставок
export interface BetsContextData {
  betForm: BetFormState;
  updateBetField: (field: keyof BetFormState, value: string) => void;
  placeBet: (battleId: string, outcomeId: string, amountInCurrency: string) => Promise<BetPlacementResult>;
  placeBetWithParams: (
    battleId: string,
    outcomeId: string,
    amount: string,
    currency: Currency,
  ) => Promise<BetPlacementResult>;
  resetBetForm: () => void;
  isSubmittingBet: boolean;
  submitBetError: Error | undefined;
  lastPlacedBet: PlacedBetData | null;
  setLastPlacedBet: (data: PlacedBetData | null) => void;
}

// Создаем контекст ставок
export const BetsContext = createContext<BetsContextData | null>(null);

// Хук для использования контекста ставок
export const useBets = () => {
  const context = useContext(BetsContext);
  if (!context) {
    throw new Error('useBets must be used within a BetsProvider');
  }
  return context;
};

// ==================== PROVIDERS ====================

// Начальное состояние формы ставки
const initialBetFormState: BetFormState = {
  amount: '10',
  currency: Currency.Usdt,
};

// Провайдер контекста баттлов
interface BattlesProviderProps {
  children: ReactNode;
}

export const BattlesProvider: React.FC<BattlesProviderProps> = ({ children }) => {
  // ID текущего баттла
  const [currentBattleId, setCurrentBattleId] = useState<string | null>(null);

  // Состояние фильтра жизненного цикла баттлов
  const [lifecycleFilter, setLifecycleFilter] = useState<GameLifecycleStage[]>(defaultLifecycleFilter);
  const [isMyBattles, setIsMyBattles] = useState(false);
  // Используем существующий хук для получения данных баттла
  const {
    battle,
    loading,
    error,
    loadPlayerStats,
    isLoadingMoreStats,
    refetch,
    loadingMoreOrders,
    fetchBettingGameWithStats,
  } = useGetBettingGame(currentBattleId || '');

  // Используем хук для получения списка баттлов
  const {
    bettingGames: battlesList,
    loading: battlesListLoading,
    error: battlesListError,
    hasNextPage,
    fetchNextPage,
    refresh,
    loadingMore,
  } = useGetBettingGames({
    lifecycle: lifecycleFilter,
    ...(isMyBattles && { isMyBattles: true }),
  });

  useEffect(() => {
    refresh();
  }, [isMyBattles]);

  // Функция для загрузки баттла по ID
  const loadBattle = useCallback((battleId: string) => {
    setCurrentBattleId(battleId);
  }, []);

  // Функция для обновления данных текущего баттла
  const refetchBattle = useCallback(async () => {
    if (refetch) {
      await refetch();
    }
  }, [refetch]);

  // Функция для загрузки списка баттлов с фильтром
  const loadBattlesList = useCallback((params?: { lifecycle?: GameLifecycleStage[]; myBattles?: boolean }) => {
    if (params?.lifecycle) {
      setLifecycleFilter(params.lifecycle);
    }
    if (params?.myBattles !== undefined) {
      setIsMyBattles(params.myBattles);
    }
  }, []);

  // Функция для обновления списка баттлов
  const refreshBattlesList = useCallback(async () => {
    if (refresh) {
      await refresh();
    }
  }, [refresh]);

  // Функция для загрузки следующей страницы баттлов
  const loadMoreBattles = useCallback(async () => {
    if (hasNextPage && !battlesListLoading && !loadingMore) {
      await fetchNextPage();
    }
  }, [hasNextPage, battlesListLoading, loadingMore, fetchNextPage]);

  // Функция для проверки возможности получения приза
  const getBattleOutcomeState = useCallback((battle: BattleData | null) => {
    if (!battle) {
      return {
        hasWinningBet: false,
        hasUnclaimedGifts: false,
        winningOutcome: null,
      };
    }

    const claimStatus = {
      hasWinningBet: false,
      hasUnclaimedGifts: false,
      winningOutcome: [...battle.outcomes].sort((a, b) => {
        return (b.result || 0) - (a.result || 0);
      })[0],
    };

    // Проверяем, что баттл завершен
    if (!battle.isCompleted) {
      return claimStatus;
    }

    // Проверяем, что у пользователя есть ставка в баттле
    if (!battle.myBets || battle.myBets.length === 0) {
      return claimStatus;
    }

    // Находим ставку
    const userBet = battle.myBets[0];
    const outcome = battle.outcomes.find((o) => o.id === userBet.outcome.id);

    if (!outcome) {
      return claimStatus;
    }

    // Проверяем, что ставка сделана на победившую карточку
    const hasWinningBet = outcome.id === claimStatus.winningOutcome.id;

    // Показываем кнопку если:
    // 1. myGifts пустой (призы еще не сгенерированы - нужно вызвать yieldGifts)
    // 2. ИЛИ есть незабранные призы (Gift.isClaimed === false)
    const hasUnclaimedGifts =
      !battle.myGifts || battle.myGifts.length === 0 || battle.myGifts.some((gift) => !gift.isClaimed);

    // Возвращаем true, если пользователь сделал ставку на победителя и можно забрать приз
    return {
      hasWinningBet,
      hasUnclaimedGifts,
      winningOutcome: claimStatus.winningOutcome,
    };
  }, []);

  // Создаем массив запросов конвертации для текущей ставки и всех валют
  const currenciesToExchange = useMemo(() => {
    // Получаем все уникальные валюты из всех активных battles
    const allCurrencies = new Set<Currency>();

    // Добавляем все валюты из всех battles

    if (battlesList) {
      battlesList.forEach((battle) => {
        if (battle.acceptedCurrencies) {
          battle.acceptedCurrencies.forEach((currency) => {
            if (currency !== Currency.Usdt) {
              allCurrencies.add(currency);
            }
          });
        }
      });
    }

    if (battle) {
      battle.acceptedCurrencies.forEach((currency) => {
        if (currency !== Currency.Usdt) {
          allCurrencies.add(currency);
        }
      });
    }

    if (allCurrencies.size === 0) {
      return [
        {
          from: Currency.Usdt,
          to: Currency.Usdt,
          amount: 1,
        },
      ];
    }

    // Создаем запросы на конвертацию для всех уникальных валют
    const exchanges = Array.from(allCurrencies).map((currency) => ({
      from: currency,
      to: Currency.Usdt,
      amount: 1,
    }));

    return exchanges;
  }, [battlesList, battle]);

  // Получаем все курсы обмена
  const { exchangedDict } = useConvertCurrencies(currenciesToExchange);

  // Функция для конвертации суммы в другие валюты с улучшенной обработкой ошибок
  const convertAmount = useCallback(
    (amount: string, from: Currency, to: Currency): number => {
      // Если суммы нет или она равна 0, возвращаем 0
      if (!amount || amount === '0') {
        return 0;
      }

      if (from === to) {
        return Number(amount) || 0;
      }

      // Получаем курс обмена
      const exchangeKey = `${from}_${to}` as Pairs;
      return (exchangedDict[exchangeKey] || 0) * Number(amount) || 0;
    },
    [exchangedDict],
  );

  const formattedBattle = useMemo(() => {
    if (!battle) return null;

    // Сумма всех ставок (без учета рейка)
    const totalPool = battle.outcomes.reduce((acc, outcome) => acc + Number(outcome.poolSum?.amount || 0), 0);
    // Призовой пул с учетом рейка
    const totalBank = totalPool * (1 - (battle.rake || DEFAULT_RAKE));
    // Сумма всех ставок пользователя
    const userBet = battle.myBets?.reduce((acc, bet) => acc + Number(bet.sum?.amount || 0), 0) || 0;

    // Потенциальный выигрыш - правильная формула
    let potentialPrize = 0;
    if (userBet > 0 && battle.myBets && battle.myBets.length > 0) {
      // Находим outcome, на который поставил пользователь
      const userBetOutcome = battle.myBets[0].outcome;
      const userOutcomeData = battle.outcomes.find((outcome) => outcome.id === userBetOutcome.id);

      if (userOutcomeData && userOutcomeData.poolSum) {
        // Общий банк карточки, на которую поставил игрок (без учета рейка)
        const playerOutcomePool = Number(userOutcomeData.poolSum.amount || 0);

        if (playerOutcomePool > 0) {
          // Формула: (сумма ставок игрока) / (общий банк карточки без рейка) × (общий банк с учетом рейка)
          potentialPrize = (userBet / playerOutcomePool) * totalBank;
        }
      }
    }

    const convertedOutcomes = battle.outcomes.map((outcome) => {
      return {
        ...outcome,
        betsNum: outcome.betsNum ?? 0,
        poolSum: outcome.poolSum
          ? {
              ...outcome.poolSum,
              amount: outcome.poolSum.amount?.toString() ?? '0',
              amountInUsdt:
                convertAmount(
                  outcome.poolSum.amount?.toString() || '0',
                  outcome.poolSum.currency || Currency.Usdt,
                  Currency.Usdt,
                ).toString() || '0',
            }
          : {
              amount: '0',
              currency: Currency.Usdt,
              amountInUsdt: '0',
            },
        matches: outcome.matches.map((match) => {
          // Получаем ID игрока для текущего outcome
          const currentPlayerId = outcome.subject?.card?.player?.id;

          // Фильтруем статистику только для текущего игрока и валидные записи
          const filteredStats = (match.playerStats || []).filter((stat) => {
            // Проверяем, что статистика принадлежит текущему игроку
            const isCurrentPlayer = stat.teamPlayer?.player?.id === currentPlayerId;
            const hasValidScore = stat.totalScore !== null && stat.totalScore !== undefined && stat.totalScore !== 0;
            return isCurrentPlayer && hasValidScore;
          });

          const totalPoints = filteredStats.reduce((sum, stat) => sum + (stat.totalScore || 0), 0);
          return {
            ...match,
            playerStats: filteredStats,
            totalPoints,
          };
        }) as (Match & { totalPoints: number })[],
      };
    });

    return {
      ...battle,
      outcomes: convertedOutcomes,
      totalBank,
      userBet,
      potentialPrize,
    };
  }, [battle, convertAmount]);

  // Мемоизируем значение контекста для оптимизации рендеринга
  const value = useMemo(
    () => ({
      currentBattle: formattedBattle,
      battleLoading: loading,
      battleError: error,
      loadBattle,
      refetchBattle,
      loadPlayerStats,
      isLoadingMoreStats,
      currentBattleId,
      loadingMoreOrders,
      battlesList: battlesList || [],
      battlesListLoading,
      battlesListError,
      loadBattlesList,
      refreshBattlesList,
      hasMoreBattles: hasNextPage || false,
      loadMoreBattles,
      loadingMoreBattles: loadingMore || false,
      getBattleOutcomeState,
      convertAmount,
      fetchBettingGameWithStats,
    }),
    [
      loadingMoreOrders,
      formattedBattle,
      loading,
      error,
      loadBattle,
      refetchBattle,
      loadPlayerStats,
      isLoadingMoreStats,
      currentBattleId,
      battlesList,
      battlesListLoading,
      battlesListError,
      loadBattlesList,
      refreshBattlesList,
      hasNextPage,
      loadMoreBattles,
      loadingMore,
      getBattleOutcomeState,
      convertAmount,
      fetchBettingGameWithStats,
    ],
  );

  return <BattlesContext.Provider value={value}>{children}</BattlesContext.Provider>;
};

// Провайдер контекста ставок
interface BetsProviderProps {
  children: ReactNode;
}

export const BetsProvider: React.FC<BetsProviderProps> = ({ children }) => {
  // Состояние формы ставки
  const [betForm, setBetForm] = useState<BetFormState>(initialBetFormState);

  // Данные о последней размещенной ставке
  const [lastPlacedBet, setLastPlacedBet] = useState<PlacedBetData | null>(null);

  // Хук для размещения ставки
  const { submitBet, loading: isSubmittingBet, error: submitBetError } = useSubmitBet();

  // Хук для создания ссылки на оплату через Telegram Stars
  const { createTelegramStarsInvoiceLink } = useCreateTelegramStarsInvoiceLink();

  // Обновление поля в форме ставки
  const updateBetField = useCallback((field: keyof BetFormState, value: string) => {
    setBetForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Сброс формы ставки
  const resetBetForm = useCallback(() => {
    setBetForm(initialBetFormState);
  }, []);

  // Размещение ставки
  const placeBet = useCallback(
    async (battleId: string, outcomeId: string, amountInCurrency: string): Promise<BetPlacementResult> => {
      // Проверяем, является ли это оплатой через Stars
      if (betForm.currency === Currency.Xtr) {
        try {
          const { data } = await createTelegramStarsInvoiceLink({
            variables: {
              currency: Currency.Usdt, // Stars всегда конвертируются в USDT
              amount: amountInCurrency,
            },
          });

          const link = data?.createTelegramStarsInvoiceLink;
          if (link) {
            return {
              success: true,
              isEnoughBalance: true,
              isStarsPayment: true,
              invoiceLink: link,
            };
          }

          console.error('❌ [BetsContext] Failed to get invoice link from response');
          return {
            success: false,
            isEnoughBalance: true,
            error: new Error('Failed to create Telegram Stars invoice'),
          };
        } catch (error) {
          console.error('💥 [BetsContext] Exception creating Stars invoice:', error);
          return {
            success: false,
            isEnoughBalance: true,
            error: error as Error,
          };
        }
      }

      // Обычная логика для других валют - размещаем ставку сразу
      try {
        await submitBet({
          variables: {
            gameId: battleId,
            outcomeId,
            amount: Number(amountInCurrency),
            currency: betForm.currency,
          },
        });

        return {
          success: true,
          isEnoughBalance: true,
        };
      } catch (error) {
        console.error('❌ [BetsContext] Failed to submit bet:', error);
        return {
          success: false,
          isEnoughBalance: true,
          error: error as Error,
        };
      }
    },
    [betForm, submitBet, createTelegramStarsInvoiceLink],
  );

  // Размещение ставки с заданными параметрами после Stars оплаты
  const placeBetWithParams = useCallback(
    async (battleId: string, outcomeId: string, amount: string, currency: Currency): Promise<BetPlacementResult> => {
      try {
        await submitBet({
          variables: {
            gameId: battleId,
            outcomeId,
            amount: Number(amount),
            currency: currency,
          },
        });

        return {
          success: true,
          isEnoughBalance: true,
        };
      } catch (error) {
        console.error('❌ [BetsContext] Failed to submit bet with params:', error);
        return {
          success: false,
          isEnoughBalance: true,
          error: error as Error,
        };
      }
    },
    [submitBet],
  );

  // Мемоизируем значение контекста для оптимизации рендеринга
  const value = useMemo(
    () => ({
      betForm,
      updateBetField,
      placeBet,
      placeBetWithParams,
      resetBetForm,
      isSubmittingBet,
      submitBetError,
      lastPlacedBet,
      setLastPlacedBet,
    }),
    [
      betForm,
      updateBetField,
      placeBet,
      placeBetWithParams,
      resetBetForm,
      isSubmittingBet,
      submitBetError,
      lastPlacedBet,
      setLastPlacedBet,
    ],
  );

  return <BetsContext.Provider value={value}>{children}</BetsContext.Provider>;
};

// Общий провайдер для всех контекстов баттлов
interface BattleContextsProviderProps {
  children: ReactNode;
}

export const BattleContextsProvider: React.FC<BattleContextsProviderProps> = ({ children }) => {
  return (
    <BattlesProvider>
      <BetsProvider>{children}</BetsProvider>
    </BattlesProvider>
  );
};
